import { useState, useEffect, useCallback } from 'react';
import { progressTracker } from '@services';

/**
 * Hook for managing user progress
 */
export function useProgress(userId, courseId) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProgress = useCallback(async () => {
    if (!userId || !courseId) {
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      
      let userProgress = await progressTracker.progressRepository.getUserProgress(userId, courseId);
      
      if (!userProgress) {
        userProgress = await progressTracker.initializeProgress(userId, courseId);
      }

      setProgress(userProgress);
      return userProgress;
    } catch (err) {
      console.error('Error loading progress:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, courseId]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const refreshProgress = useCallback(() => {
    return loadProgress();
  }, [loadProgress]);

  return { progress, loading, error, refreshProgress };
}

/**
 * Hook for getting section progress
 */
export function useSectionProgress(userId, courseId, sectionId) {
  const [sectionProgress, setSectionProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !courseId || !sectionId) {
      setLoading(false);
      return;
    }

    const loadSectionProgress = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await progressTracker.progressRepository.getSectionProgress(
          userId,
          courseId,
          sectionId
        );

        setSectionProgress(data);
      } catch (err) {
        console.error('Error loading section progress:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadSectionProgress();
  }, [userId, courseId, sectionId]);

  return { sectionProgress, loading, error };
}

/**
 * Hook for getting question progress
 */
export function useQuestionProgress(userId, courseId, questionId) {
  const [questionProgress, setQuestionProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !courseId || !questionId) {
      setLoading(false);
      return;
    }

    const loadQuestionProgress = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await progressTracker.progressRepository.getQuestionProgress(
          userId,
          courseId,
          questionId
        );

        setQuestionProgress(data);
      } catch (err) {
        console.error('Error loading question progress:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadQuestionProgress();
  }, [userId, courseId, questionId]);

  return { questionProgress, loading, error };
}

/**
 * Hook for getting progress report
 */
export function useProgressReport(userId, courseId) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReport = useCallback(async () => {
    if (!userId || !courseId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const reportData = await progressTracker.getProgressReport(userId, courseId);
      setReport(reportData);
    } catch (err) {
      console.error('Error loading progress report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, courseId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const refreshReport = useCallback(() => {
    return loadReport();
  }, [loadReport]);

  return { report, loading, error, refreshReport };
}
